// File: frontend/src/serviceWorkerRegistration.ts
// Registers /service-worker.js so the app can be installed and used offline.
// Called once from src/index.tsx after the app renders.
//
// Ticket 4: also detects when a newer service worker has been deployed and is
// waiting to take over, and exposes that state to the UI through
// subscribeToUpdates() so the app can show a "new version available" banner.
// applyUpdate() tells the waiting worker to activate and reloads the page.

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.match(/^\[::1\]$/) !== null
);

// ---------------------------------------------------------------------------
// Update detection (Ticket 4)
// ---------------------------------------------------------------------------

type UpdateListener = (isUpdateAvailable: boolean) => void;

// The service worker that finished installing but is still in "waiting" state,
// i.e. the newly deployed version that has not taken over the page yet.
let waitingWorker: ServiceWorker | null = null;

const updateListeners = new Set<UpdateListener>();

// Set only when the user explicitly asked for the update. The controllerchange
// event also fires on a first-ever install (because the worker calls
// clients.claim()), and reloading in that case would restart the page for every
// brand new visitor for no reason.
let updateRequested = false;

// Guards against an endless reload loop if controllerchange fires more than once.
let reloading = false;

// How often an open tab asks the server whether a newer service worker exists.
// Without this, a tab left open for hours would never notice a deploy.
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

// Minimum gap between two checks triggered by the tab regaining focus, so
// alt-tabbing repeatedly does not hammer the server.
const FOCUS_CHECK_THROTTLE_MS = 60 * 1000;

let lastCheckAt = 0;

/**
 * Subscribes to "a new version is waiting" changes.
 * The listener is invoked immediately with the current state so a component
 * that mounts after the update was already detected still sees it.
 * Returns an unsubscribe function.
 */
export function subscribeToUpdates(listener: UpdateListener): () => void {
  updateListeners.add(listener);
  listener(waitingWorker !== null);
  return () => {
    updateListeners.delete(listener);
  };
}

function setWaitingWorker(worker: ServiceWorker | null): void {
  waitingWorker = worker;
  updateListeners.forEach((listener) => listener(waitingWorker !== null));
}

/**
 * Activates the waiting service worker and reloads into the new build.
 * Safe to call even if nothing is waiting — it then just reloads.
 */
export function applyUpdate(): void {
  if (!waitingWorker) {
    window.location.reload();
    return;
  }

  updateRequested = true;

  // The new worker stays in "waiting" until it is told to take over; once it
  // does, controllerchange fires and the listener below reloads the page.
  waitingWorker.postMessage({ type: 'SKIP_WAITING' });

  // Fallback: if the worker never reports back (old browser, message dropped),
  // reload anyway rather than leaving the banner stuck on screen.
  window.setTimeout(() => {
    if (!reloading) {
      reloading = true;
      window.location.reload();
    }
  }, 3000);
}

/**
 * Asks the browser to re-fetch service-worker.js and compare it with the
 * installed one. Throttled so it cannot be triggered in a tight loop.
 */
function checkForUpdate(registration: ServiceWorkerRegistration, force: boolean): void {
  const now = Date.now();
  if (!force && now - lastCheckAt < FOCUS_CHECK_THROTTLE_MS) {
    return;
  }
  lastCheckAt = now;
  registration.update().catch(() => {
    // Offline or the server is unreachable — nothing to do, the next check
    // will pick the update up.
  });
}

export function register(): void {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // Running on localhost: verify the service worker still exists before registering,
        // so a stale one from a previous project on the same port does not linger.
        checkValidServiceWorker(swUrl);
      } else {
        registerValidSW(swUrl);
      }

      // Fires when the waiting worker calls skipWaiting() and takes control.
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!updateRequested || reloading) {
          return;
        }
        reloading = true;
        window.location.reload();
      });
    });
  }
}

function registerValidSW(swUrl: string): void {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // A new version may already be waiting from a previous page load — for
      // example when the user navigated away before clicking the banner.
      if (registration.waiting && navigator.serviceWorker.controller) {
        setWaitingWorker(registration.waiting);
      }

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content is available; it will be used once all tabs are closed,
              // or immediately if the user accepts the update banner.
              console.log('New content is available and will be used when all tabs for this page are closed.');
              setWaitingWorker(registration.waiting ?? installingWorker);
            } else {
              // Content is cached for offline use for the first time.
              console.log('Content is cached for offline use.');
            }
          }
        };
      };

      // Poll for a newer deploy while the tab stays open.
      window.setInterval(() => {
        checkForUpdate(registration, true);
      }, UPDATE_CHECK_INTERVAL_MS);

      // Also check whenever the user comes back to the tab, which is when a
      // stale build is most likely to be noticed.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          checkForUpdate(registration, false);
        }
      });
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl: string): void {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Unregister any existing one and reload.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}